import { addDoc, collection, doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { auth, db } from '../firebase';
import { copyAppLink } from '../utils/copyAppLink';

const styles = {
  screen: {
    minHeight: '100vh',
    backgroundColor: '#F4F6F9',
    fontFamily: 'system-ui, sans-serif',
  },
  main: {
    marginLeft: 220,
    padding: 24,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0A2D5A',
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 13,
    color: '#888',
    marginBottom: 24,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: 12,
    border: '0.5px solid #e0e0e0',
    padding: 24,
    maxWidth: 600,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0A2D5A',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 16,
    marginTop: 24,
    paddingBottom: 8,
    borderBottom: '1px solid #eee',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 16,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: '600',
    color: '#555',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: 'white',
  },
  planToggle: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
  },
  planBtn: {
    flex: 1,
    padding: '10px',
    borderRadius: 8,
    border: '1.5px solid #ddd',
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: '#888',
  },
  planBtnActive: {
    flex: 1,
    padding: '10px',
    borderRadius: 8,
    border: '1.5px solid #1D6FA4',
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: '#E6F1F9',
    color: '#1D6FA4',
  },
  planBtnActiveOrange: {
    flex: 1,
    padding: '10px',
    borderRadius: 8,
    border: '1.5px solid #E8620A',
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: '#FDF0E8',
    color: '#E8620A',
  },
  submitBtn: {
    backgroundColor: '#E8620A',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 8,
  },
  cancelBtn: {
    backgroundColor: 'white',
    color: '#888',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 8,
    marginLeft: 12,
  },
  error: {
    backgroundColor: '#FEE2E2',
    color: '#CC0000',
    padding: '12px 16px',
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 16,
  },
  previewCard: {
    backgroundColor: '#F4F6F9',
    borderRadius: 10,
    border: '1px solid #e0e0e0',
    padding: 20,
    marginTop: 24,
  },
  previewTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0A2D5A',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #ddd',
  },
  previewItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 10,
  },
  previewDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginTop: 5,
    flexShrink: 0,
  },
  previewLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#0A2D5A',
  },
  previewDate: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  previewEmpty: {
    fontSize: 13,
    color: '#aaa',
    fontStyle: 'italic',
  },
  generatingPanel: {
    textAlign: 'center',
    padding: '48px 0',
    fontSize: 16,
    color: '#555',
    fontWeight: '600',
  },
  codePanel: {
    textAlign: 'center',
    padding: '24px 0',
  },
  codeCheck: {
    color: '#3B6D11',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 24,
  },
  codeLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 14,
  },
  codeBoxes: {
    display: 'flex',
    gap: 8,
    justifyContent: 'center',
    marginBottom: 18,
  },
  codeBox: {
    width: 46,
    height: 58,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    fontWeight: '700',
    color: '#0A2D5A',
    backgroundColor: '#F4F6F9',
    border: '1px solid #ddd',
    borderRadius: 8,
    textTransform: 'uppercase',
    fontFamily: 'monospace',
  },
  codeHint: {
    fontSize: 13,
    color: '#888',
    marginBottom: 28,
    lineHeight: 1.6,
    maxWidth: 420,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  codeButtons: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
  },
  copyLinkBtn: {
    backgroundColor: '#2E7D32',
    color: 'white',
    border: '1px solid #2E7D32',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: 24,
  },
  toast: {
    position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
    backgroundColor: '#0A2D5A', color: 'white', padding: '12px 24px', borderRadius: 10,
    fontSize: 14, fontWeight: '600', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 2000,
    display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap',
  },
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

function formatPremium(value) {
  if (!value) return '';
  const num = parseFloat(String(value).replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return '';
  return '$' + num.toFixed(2);
}

function buildReminderPreview(planType, startDate, reviewInterval, birthdayMonth, birthdayDay) {
  const reminders = [];

  if (!planType) return reminders;

  if (planType === 'Supplement') {
    if (startDate) {
      const [year, month, day] = startDate.split('-').map(Number);
      const today = new Date();
      const interval = parseInt(reviewInterval || 1);
      let reviewYear = year;
      while (new Date(reviewYear, month - 1, day) <= today) {
        reviewYear += interval;
      }
      const reviewDate = new Date(reviewYear, month - 1, day);
      const reviewYearDisplay = reviewDate.getFullYear();
      const notifyDate = new Date(reviewDate);
      notifyDate.setDate(notifyDate.getDate() - 30);
      const notifyMonth = MONTHS[notifyDate.getMonth()];
      const notifyDay = notifyDate.getDate();
      reminders.push({
        label: 'Annual Plan Review',
        date: `Notification: ${notifyMonth} ${notifyDay}, ${reviewYearDisplay} (30 days before anniversary)`,
        dot: '#E8620A',
      });
    }

    reminders.push({
      label: 'Watch for Part D Letter',
      date: 'September 5 — push notification fires',
      dot: '#1D6FA4',
    });

    reminders.push({
      label: 'AEP Does Not Affect Your Supplement Plan',
      date: 'September 28 — push notification fires',
      dot: '#0A2D5A',
    });

    reminders.push({
      label: 'Part D Review Window Opens in 5 Days',
      date: 'October 10 — if Part D toggle ON in your profile',
      dot: '#1D6FA4',
    });

    reminders.push({
      label: 'Final Part D Notice',
      date: 'November 1 — if Part D toggle ON and no response',
      dot: '#E8620A',
    });
  }

  if (planType === 'Advantage') {
    reminders.push({
      label: 'Watch for Your ANOC Letter',
      date: 'September 1 — push notification fires',
      dot: '#1D6FA4',
    });

    reminders.push({
      label: 'Have You Received Your Notice?',
      date: 'October 1 — push notification fires',
      dot: '#1D6FA4',
    });

    reminders.push({
      label: 'AEP is Open',
      date: 'October 15 — Keep My Plan or Schedule a Review',
      dot: '#1D6FA4',
    });

    reminders.push({
      label: 'AEP Closes December 7th',
      date: 'December 1 — final push notification',
      dot: '#E8620A',
    });

    reminders.push({
      label: 'Open Enrollment Period',
      date: 'January 15 — only if no AEP response',
      dot: '#1D6FA4',
    });
  }

  if (birthdayMonth && birthdayDay) {
    const monthName = MONTHS[parseInt(birthdayMonth) - 1];
    reminders.push({
      label: 'Happy Birthday',
      date: `${monthName} ${birthdayDay} — push notification fires on their birthday`,
      dot: '#E8620A',
    });
  }

  return reminders;
}

export default function AddClient() {
  const navigate = useNavigate();
  const [planType, setPlanType] = useState('Supplement');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newCode, setNewCode] = useState(null);
  const [savedName, setSavedName] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [toast, setToast] = useState(null);

  const unsubRef = useRef(null);
  const timeoutRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    mobile: '',
    email: '',
    state: 'WI',
    carrier: '',
    plan: '',
    premium: '',
    startDate: '',
    reviewInterval: '1',
    birthdayMonth: '',
    birthdayDay: '',
    notes: '',
  });

  useEffect(() => {
    return () => {
      if (unsubRef.current) unsubRef.current();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const resetForAnother = () => {
    setNewCode(null);
    setCodeError(false);
    setError('');
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  const handleCopyAppLink = (clientName) => {
    const result = copyAppLink(newCode);
    if (!result.ok) {
      showToast('Code still generating — try again in a moment');
      return;
    }
    showToast(`✓  App link copied for ${clientName}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCodeError(false);
    setNewCode(null);

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Email format is invalid.');
      return;
    }

    setLoading(true);

    const clientName = form.name;

    try {
      const docRef = await addDoc(collection(db, 'clients'), {
        agentId: auth.currentUser.uid,
        name: form.name,
        mobile: form.mobile,
        email: form.email.trim(),
        state: form.state,
        planType: planType,
        carrier: form.carrier,
        plan: form.plan,
        premium: formatPremium(form.premium),
        startDate: form.startDate,
        reviewInterval: form.reviewInterval,
        birthdayMonth: form.birthdayMonth,
        birthdayDay: form.birthdayDay,
        notes: form.notes,
        aepStatus: null,
        createdAt: Timestamp.now(),
      });

      setSavedName(clientName);
      setForm({
        name: '', mobile: '', email: '', state: 'WI', carrier: '', plan: '',
        premium: '', startDate: '', reviewInterval: '1',
        birthdayMonth: '', birthdayDay: '', notes: '',
      });
      setPlanType('Supplement');

      unsubRef.current = onSnapshot(doc(db, 'clients', docRef.id), (snap) => {
        const data = snap.data();
        if (data && data.lookupCode) {
          setNewCode(data.lookupCode);
          setLoading(false);
          if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
          if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        }
      });

      timeoutRef.current = setTimeout(() => {
        if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
        setLoading(false);
        setCodeError(true);
      }, 15000);

    } catch (err) {
      setError('Error saving client. Please try again.');
      setLoading(false);
    }
  };

  const reminders = buildReminderPreview(
    planType,
    form.startDate,
    form.reviewInterval,
    form.birthdayMonth,
    form.birthdayDay
  );

  return (
    <div style={styles.screen}>
      <Sidebar />
      {toast && <div style={styles.toast}>{toast}</div>}
      <div style={styles.main}>
        <div style={styles.pageTitle}>Add New Client</div>
        <div style={styles.pageSub}>Fill in the client's plan details to get them set up.</div>

        <div style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          {loading ? (
            <div style={styles.generatingPanel}>
              Saving client and generating their access code…
            </div>
          ) : newCode ? (
            <div style={styles.codePanel}>
              <div style={styles.codeCheck}>✓ {savedName} added</div>
              <div style={styles.codeLabel}>Client Access Code</div>
              <div style={styles.codeBoxes}>
                {newCode.split('').map((ch, i) => (
                  <div key={i} style={styles.codeBox}>{ch}</div>
                ))}
              </div>
              <button style={styles.copyLinkBtn} type="button" onClick={() => handleCopyAppLink(savedName)}>
                Copy App Link
              </button>
              <div style={styles.codeHint}>
                Read this code to your client when they open the app, or text them
                the link you just copied — either way, their plan loads every time
                after that.
              </div>
              <div style={styles.codeButtons}>
                <button style={styles.submitBtn} type="button" onClick={resetForAnother}>
                  Add Another Client
                </button>
                <button style={styles.cancelBtn} type="button" onClick={() => navigate('/')}>
                  Done
                </button>
              </div>
            </div>
          ) : codeError ? (
            <div style={styles.codePanel}>
              <div style={styles.codeCheck}>✓ {savedName} added</div>
              <div style={styles.codeHint}>
                Their access code is still generating. Open My Clients in a moment
                to see it.
              </div>
              <div style={styles.codeButtons}>
                <button style={styles.submitBtn} type="button" onClick={resetForAnother}>
                  Add Another Client
                </button>
                <button style={styles.cancelBtn} type="button" onClick={() => navigate('/')}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={styles.sectionTitle}>Client Information</div>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Full Name</label>
                  <input style={styles.input} value={form.name}
                    onChange={e => handleChange('name', e.target.value)}
                    placeholder="Dorothy Walters" required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Mobile Number</label>
                  <input style={styles.input} value={form.mobile}
                    onChange={e => handleChange('mobile', e.target.value)}
                    placeholder="(920) 555-0100" required />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Email (Optional)</label>
                <input style={styles.input} value={form.email}
                  onChange={e => handleChange('email', e.target.value)}
                  placeholder="dorothy@email.com" />
              </div>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>State</label>
                  <select style={styles.select} value={form.state}
                    onChange={e => handleChange('state', e.target.value)}>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Birthday (Optional)</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select style={{ ...styles.select, flex: 1 }} value={form.birthdayMonth}
                      onChange={e => handleChange('birthdayMonth', e.target.value)}>
                      <option value="">Month</option>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <select style={{ ...styles.select, width: 80 }} value={form.birthdayDay}
                      onChange={e => handleChange('birthdayDay', e.target.value)}>
                      <option value="">Day</option>
                      {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div style={styles.sectionTitle}>Plan Information</div>
              <div style={styles.field}>
                <label style={styles.label}>Plan Type</label>
                <div style={styles.planToggle}>
                  <button type="button"
                    style={planType === 'Supplement' ? styles.planBtnActive : styles.planBtn}
                    onClick={() => setPlanType('Supplement')}>
                    Medicare Supplement
                  </button>
                  <button type="button"
                    style={planType === 'Advantage' ? styles.planBtnActiveOrange : styles.planBtn}
                    onClick={() => setPlanType('Advantage')}>
                    Medicare Advantage
                  </button>
                </div>
              </div>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Carrier</label>
                  <input style={styles.input} value={form.carrier}
                    onChange={e => handleChange('carrier', e.target.value)}
                    placeholder="Mutual of Omaha" required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Plan</label>
                  <input style={styles.input} value={form.plan}
                    onChange={e => handleChange('plan', e.target.value)}
                    placeholder="Plan N" required />
                </div>
              </div>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Monthly Premium</label>
                  <input style={styles.input} value={form.premium}
                    onChange={e => handleChange('premium', e.target.value)}
                    onBlur={e => handleChange('premium', formatPremium(e.target.value))}
                    placeholder="$147.00" required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Plan Start Date</label>
                  <input style={styles.input} type="date" value={form.startDate}
                    onChange={e => handleChange('startDate', e.target.value)} required />
                </div>
              </div>

              {planType === 'Supplement' && (
                <div style={styles.field}>
                  <label style={styles.label}>Review Interval</label>
                  <select style={styles.select} value={form.reviewInterval}
                    onChange={e => handleChange('reviewInterval', e.target.value)}>
                    <option value="1">Every year</option>
                    <option value="2">Every 2 years</option>
                    <option value="3">Every 3 years</option>
                  </select>
                </div>
              )}

              <div style={styles.sectionTitle}>Notes</div>
              <div style={styles.field}>
                <label style={styles.label}>Agent Notes (not visible to client)</label>
                <textarea style={{ ...styles.input, height: 80, resize: 'vertical' }}
                  value={form.notes}
                  onChange={e => handleChange('notes', e.target.value)}
                  placeholder="Spoke 10/3, staying with Humana..." />
              </div>

              <div style={styles.previewCard}>
                <div style={styles.previewTitle}>Reminders This Client Will Receive</div>
                {reminders.length === 0 ? (
                  <div style={styles.previewEmpty}>
                    Select a plan type to see the reminder schedule.
                  </div>
                ) : (
                  reminders.map((r, i) => (
                    <div key={i} style={styles.previewItem}>
                      <div style={{ ...styles.previewDot, backgroundColor: r.dot }} />
                      <div>
                        <div style={styles.previewLabel}>{r.label}</div>
                        <div style={styles.previewDate}>{r.date}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div>
                <button style={styles.submitBtn} type="submit">
                  Save Client
                </button>
                <button style={styles.cancelBtn} type="button" onClick={() => navigate('/')}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}